import React, { useEffect, useRef } from 'react';
import Window from '../window-system/Window';
import './BattleLogWindow.css';

interface BattleLogEvent {
  type: string;
  attacker?: string;
  target?: string;
  zone?: string;
  outcome?: {
    reaction: string;
    damage?: number;
    isCritical?: boolean;
  };
  // Поля для расширенной системы
  abilityName?: string;
  effectName?: string;
  value?: number;
  round?: number; // Добавляем поле round для события round-start
  damage?: number; // Урон от способностей
  source?: string; // Источник эффекта (для DoT)
  sourceName?: string; // Название способности/пассивки
}

interface BattleLogWindowProps {
  logs: BattleLogEvent[];
  characterId: string;
  opponentNickname?: string;
}

const getZoneName = (zone: string) => {
  const names: Record<string, string> = {
    head: 'Голова',
    body: 'Тело',
    legs: 'Ноги'
  };
  return names[zone] || zone;
};

const BattleLogWindow: React.FC<BattleLogWindowProps> = ({
  logs,
  characterId,
  opponentNickname = 'Оппонент'
}) => {
  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <div className="battle-log-content" ref={logContainerRef}>
      {logs.map((event, index) => {
          let message = '';
          let className = '';
          
          const attackerName = event.attacker === characterId ? 'Вы' : opponentNickname;
          const targetName = event.target === characterId ? 'вас' : opponentNickname;
          
          // Обработка разных типов событий
          switch (event.type) {
            case 'round-start':
              message = `=== Раунд ${event.round || event.value} ===`;
              className = 'log-round';
              break;
              
            case 'ability':
              if (event.abilityName) {
                message = `${attackerName} использует способность "${event.abilityName}"`;
                if (event.damage && event.damage > 0) {
                  message += ` и наносит ${event.damage} урона`;
                }
                className = 'log-ability';
              }
              break;
              
            case 'effect':
              if (event.effectName) {
                message = `Эффект "${event.effectName}"`;
                if (event.value) {
                  message += `: ${event.value}`;
                }
                className = 'log-effect';
              }
              break;
              
            case 'dot':
              if (event.damage) {
                const effectTarget = event.target === characterId ? 'Вы получаете' : `${opponentNickname} получает`;
                const sourceName = event.sourceName || event.effectName || 'неизвестный источник';
                message = `Урон от ${sourceName}: ${effectTarget} ${event.damage} урона`;
                className = 'log-dot';
              }
              break;
              
            case 'attack':
              if (!event.outcome || !event.zone) break;
              
              message = `${attackerName} атакует `;
              
              switch (event.outcome.reaction) {
                case 'block':
                  message += `${targetName} в ${getZoneName(event.zone)}, но ${targetName} блокирует!`;
                  if (event.outcome.damage && event.outcome.damage > 0) {
                    message += ` (${event.outcome.damage} урона)`;
                  }
                  className = 'log-block';
                  break;
                case 'dodge':
                  message += `${targetName} в ${getZoneName(event.zone)}, но ${targetName} уклоняется!`;
                  className = 'log-dodge';
                  break;
                case 'miss':
                  message += `${targetName} в ${getZoneName(event.zone)}, но промахивается!`;
                  className = 'log-miss';
                  break;
                case 'hit':
                  if (event.outcome.isCritical) {
                    message += `и наносит критический удар! ${event.outcome.damage} урона по ${targetName}`;
                    className = 'log-crit';
                  } else {
                    message += `и наносит ${event.outcome.damage} урона по ${targetName}`;
                    className = 'log-hit';
                  }
                  break;
                case 'death':
                  message += `и наносит смертельный удар по ${targetName}!`;
                  className = 'log-death';
                  break;
              }
              break;
              
            default:
              // Для неизвестных событий показываем как есть
              if (event.type) {
                message = `[${event.type}]`;
                if (event.value) message += `: ${event.value}`;
                className = 'log-default';
              }
          }
          
          if (message) {
            return <p key={index} className={className}>{message}</p>;
          }
          return null;
        })}
    </div>
  );
};

export default BattleLogWindow;
